import { useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { connectSSE, disconnectSSE } from "Utils/sse";
import {
  NOTIFICATION_RECEIVED,
  SSE_CONNECTED,
  SSE_CONNECTING,
  SSE_DISCONNECTED,
  SSE_ERROR,
  MARK_ALL_SSE_READ,
  CLEAR_SSE_NOTIFICATIONS,
} from "Redux/ActionTypes";

const isExpectedAbort = (error) =>
  error?.name === "AbortError" ||
  String(error?.message || "").includes("BodyStreamBuffer was aborted");

export const useSSENotifications = ({ enabled = true, onNotification } = {}) => {
  const dispatch = useDispatch();
  const errorReportedRef = useRef(false);
  const sseState = useSelector((state) => state.sseNotifications);

  const handleNotification = useCallback(
    (data) => {
      if (Array.isArray(data)) {
        data.forEach((item) => {
          dispatch({ type: NOTIFICATION_RECEIVED, payload: item });
          onNotification?.(item);
        });
        return;
      }

      dispatch({ type: NOTIFICATION_RECEIVED, payload: data });
      onNotification?.(data);
    },
    [dispatch, onNotification]
  );

  const handleError = useCallback(
    (error) => {
      if (isExpectedAbort(error)) {
        return;
      }

      if (errorReportedRef.current) return;
      errorReportedRef.current = true;
      console.warn("[SSE] Notification connection unavailable:", error);
      dispatch({ type: SSE_ERROR, payload: error?.message || "SSE Error" });
    },
    [dispatch]
  );

  const handleOpen = useCallback(() => {
    errorReportedRef.current = false;
    dispatch({ type: SSE_CONNECTED });
  }, [dispatch]);

  const connect = useCallback(async () => {
    dispatch({ type: SSE_CONNECTING });
    await connectSSE(handleNotification, handleError, handleOpen);
  }, [dispatch, handleNotification, handleError, handleOpen]);

  const disconnect = useCallback(() => {
    disconnectSSE();
    dispatch({ type: SSE_DISCONNECTED });
  }, [dispatch]);

  const markAllAsRead = useCallback(() => {
    dispatch({ type: MARK_ALL_SSE_READ });
  }, [dispatch]);

  const clearAll = useCallback(() => {
    dispatch({ type: CLEAR_SSE_NOTIFICATIONS });
  }, [dispatch]);

  useEffect(() => {
    if (enabled) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [enabled]);

  return {
    ...sseState,
    connect,
    disconnect,
    markAllAsRead,
    clearAll,
  };
};
